import { useCallback, useEffect, useMemo, useState } from 'react'

import type { FocusState, SelectedCategory } from '../../../types'
import type { InteractionState } from '../types'

type UseKeyboardNavigationProps = {
  interactionState: InteractionState
  options: SelectedCategory[]
  searchValue: string
  selectedCategory: SelectedCategory
  onNavigateUp: () => void
  onNavigateDown: (category: SelectedCategory) => void
  onSelect: (category: SelectedCategory) => void
  onExit: () => void
}

export const useKeyboardNavigation = ({
  interactionState,
  options,
  searchValue,
  selectedCategory,
  onNavigateUp,
  onNavigateDown,
  onSelect,
  onExit,
}: UseKeyboardNavigationProps) => {
  const getInitialFocus = useCallback(
    (category: SelectedCategory, opts: SelectedCategory[]): FocusState => {
      if (!category || !opts.length) {
        return { index: null, type: null }
      }

      const selectedIndex = opts.findIndex((option) => option && option.id === category.id)
      if (selectedIndex === -1) {
        return { index: null, type: null }
      }

      return {
        index: selectedIndex,
        type: 'row',
      }
    },
    [],
  )

  const [focus, setFocus] = useState<FocusState>(() => getInitialFocus(selectedCategory, options))

  const hasHeader = useMemo(
    () => !!selectedCategory?.breadcrumbs?.length,
    [selectedCategory?.breadcrumbs?.length],
  )

  useEffect(() => {
    setFocus(getInitialFocus(selectedCategory, options))
  }, [options, selectedCategory, searchValue, getInitialFocus])

  const handleNavigateDown = useCallback(() => {
    if (focus.index === null) {
      if (hasHeader) {
        setFocus({ index: -1, type: 'header' })
      } else {
        setFocus({ index: 0, type: 'row' })
      }

      return
    }

    if (focus.type === 'header') {
      setFocus({ index: 0, type: 'row' })

      return
    }

    const nextIndex = focus.index + 1
    if (nextIndex >= options.length) {
      if (hasHeader) {
        setFocus({ index: -1, type: 'header' })
      } else {
        setFocus({ index: 0, type: 'row' })
      }
    } else {
      setFocus({ index: nextIndex, type: 'row' })
    }
  }, [focus.index, focus.type, hasHeader, options, searchValue])

  const handleNavigateUp = useCallback(() => {
    if (focus.index === null) {
      setFocus({
        index: options.length - 1,
        type: 'row',
      })

      return
    }

    if (focus.index === 0 && focus.type === 'row' && hasHeader) {
      setFocus({ index: -1, type: 'header' })

      return
    }

    const prevIndex = focus.index - 1
    if (prevIndex < 0) {
      if (hasHeader) {
        setFocus({ index: -1, type: 'header' })
      } else {
        setFocus({
          index: options.length - 1,
          type: 'row',
        })
      }
    } else {
      setFocus({ index: prevIndex, type: 'row' })
    }
  }, [focus.index, focus.type, hasHeader, options.length, searchValue])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!interactionState || !options.length) return

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault()
          handleNavigateDown()
          break
        }

        case 'ArrowUp': {
          e.preventDefault()
          handleNavigateUp()
          break
        }

        case 'ArrowRight': {
          e.preventDefault()
          if (focus.index === null) return

          if (focus.type === 'row') {
            onNavigateDown(options[focus.index])
          }
          break
        }

        case 'ArrowLeft': {
          e.preventDefault()
          if (focus.type === 'header') {
            onNavigateUp()

            return
          }

          if (focus.type === 'row' && hasHeader) {
            setFocus({ index: -1, type: 'header' })
          }
          break
        }

        case 'Enter': {
          e.preventDefault()
          if (focus.index === null) return

          if (focus.type === 'header') {
            onNavigateUp()
          } else if (focus.type === 'row') {
            const focusedOption = options[focus.index]
            const hasChildren = Boolean(focusedOption?.children?.length) && !searchValue

            if (hasChildren) {
              onNavigateDown(focusedOption)
              setFocus({ index: -1, type: 'header' })
            } else {
              onSelect(focusedOption)
            }
          }
          break
        }

        case 'Tab':
        case 'Escape': {
          if (e.key === 'Escape') e.preventDefault()
          onExit()
          break
        }
      }
    },
    [
      interactionState,
      options,
      focus.index,
      focus.type,
      searchValue,
      hasHeader,
      handleNavigateDown,
      handleNavigateUp,
      onNavigateDown,
      onNavigateUp,
      onSelect,
      onExit,
    ],
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  return focus
}
